import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router';
import { toast } from 'react-toastify';
import { RootState } from '../../store/store';
import { UserRoleEnum } from '../../types/apiGeneratedTypes';
import Button from '../../components/BaseComponents/Button';
import Loader from '../../components/BaseComponents/Loader';
import TeamModal from '../../components/Teams/TeamModal';
import TeamViewModal from '../../components/Teams/TeamViewModal';
import TeamsTable from '../../components/Teams/TeamsTable';
import {
  getTeamsList,
  Team,
} from '../../api/teams';
import './TeamsPage.scss';

const isSuperAdminUser = (admin: RootState['admin']['admin']) =>
  admin.role === UserRoleEnum.SuperAdmin && admin.is_super_admin === true;

const TeamsPage: React.FC = () => {
  const LIMIT = 10;
  const admin = useSelector((state: RootState) => state.admin.admin);
  const [teams, setTeams] = useState<Team[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [teamToEdit, setTeamToEdit] = useState<Team | null>(null);
  const [teamToView, setTeamToView] = useState<Team | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const offsetRef = useRef<number>(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null!);

  const fetchTeams = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      offsetRef.current = 0;
      const response = await getTeamsList(0, LIMIT);
      setTeams(response.teams);
      setTotalCount(response.totalCount);
      setHasMore(response.teams.length < response.totalCount);
    } catch (e: any) {
      const errorMessage = e?.message || 'Failed to load teams';
      setLoadError(errorMessage);
      toast.error(errorMessage);
      setTeams([]);
      setTotalCount(0);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMoreTeams = useCallback(async () => {
    if (isLoading || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    try {
      offsetRef.current += LIMIT;
      const response = await getTeamsList(offsetRef.current, LIMIT);
      if (response.teams.length > 0) {
        setTeams((prev) => [...prev, ...response.teams]);
        setTotalCount(response.totalCount);
        setHasMore(offsetRef.current + response.teams.length < response.totalCount);
      } else {
        setHasMore(false);
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load teams');
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoading, isLoadingMore]);

  useEffect(() => {
    if (isSuperAdminUser(admin)) {
      fetchTeams();
    }
  }, [admin, fetchTeams]);

  const onTableScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || isLoading || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 100;
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreTeams();
      }
    }, 150);
  }, [hasMore, isLoading, loadMoreTeams]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', onTableScroll, { passive: true });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', onTableScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [onTableScroll]);

  const onAddClick = () => {
    setTeamToEdit(null);
    setIsModalOpen(true);
  };

  const onView = (team: Team) => {
    setTeamToView(team);
  };

  const onCloseViewModal = () => {
    setTeamToView(null);
  };

  const onEdit = (team: Team) => {
    setTeamToEdit(team);
    setIsModalOpen(true);
  };

  const onModalClose = () => {
    setIsModalOpen(false);
    setTeamToEdit(null);
  };

  const onSaved = (team: Team, mode: 'create' | 'update') => {
    if (mode === 'create') {
      setTeams((prev) => [...prev, team]);
      setTotalCount((prev) => prev + 1);
    } else {
      setTeams((prev) =>
        prev.map((t) => (t.id === team.id ? team : t))
      );
    }
  };

  if (!isSuperAdminUser(admin)) {
    return <Navigate to='/members' replace />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className='page-container'>
      <div className='search-row-container sideBarPad'>
        <div className='search-and-filter-container'>
          <h2 className='teams-title'>Teams ({totalCount})</h2>
        </div>
        <Button
          isSecondary={true}
          text='Add teams'
          onClick={onAddClick}
        />
      </div>
      {loadError && (
        <div style={{ marginBottom: 16, color: '#c00' }}>{loadError}</div>
      )}
      <TeamsTable
        teams={teams}
        onView={onView}
        onEdit={onEdit}
        tableBodyRef={scrollContainerRef}
      />
      <TeamViewModal
        isOpen={teamToView !== null}
        onClose={onCloseViewModal}
        team={teamToView}
      />
      <TeamModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        teamToEdit={teamToEdit}
        onSaved={onSaved}
      />
    </div>
  );
};

export default TeamsPage;
